import { Component, OnInit } from '@angular/core';
import {
  AddProductRequest, ArtifactVersionItem, GetProductRequest, IProduct,
  ListPagedArtifactsRequest,
  ListPagedArtifactsResponse,
  SituViewWebApiClient, UpdateProductMatchRequest, UpdateProductMatchResponse, UpdateProductRequest
} from 'situview-webapi-client';
import { ArtifactService, AuthService, BreadcrumbService, Utils, ProductService, StateService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GetReportRequest, GetReportResponse, ReportType, SituAnalyticsWebApiClient } from 'situanalytics-webapi-client';
import { Company } from 'identity-webapi-client';
import { Subscription } from 'rxjs';
import { GetProductResponse } from 'situview-webapi-client';

@Component({
  selector: 'products-add-edit',
  templateUrl: './products-add-edit.component.html',
  styleUrls: ['./products-add-edit.component.less']
})

export class ProductsAddEditComponent implements OnInit {
  admin = false;
  productForm: FormGroup;
  productSku: string | undefined;
  submitted = false;
  isAddMode: boolean = false;
  id: string = '';
  product: IProduct | undefined;
  copyProduct: IProduct | undefined;
  company: string = '';
  isDarkMode: boolean = true;
  selectedCompany: Company | undefined;
  isNotHomeFeed: boolean = false;

  artifactsLoading: boolean = false;
  searchArtifactField = new FormControl();
  filteredProductArtifacts: LinkableProduct[] | undefined;
  productArtifacts: LinkableProduct[] | undefined;

  colorPool: string[] = [];
  totalsChartLoading: boolean = false;
  public totalsChartLabels: string[] = [];
  public totalsChartData: any[] = [];
  public totalsChartColors: Array<any> = [];

  viewsChartLoading: boolean = false;
  public viewsChartData: Array<any> = [];
  public viewsChartLabels: Array<string> = [];
  public viewsChartColors: Array<any> = [];

  private _subscriptions: Subscription[] = []; 

  private _loading = false;
  public get loading(): boolean { return this._loading; }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private situViewClient: SituViewWebApiClient,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private situAnalyticsClient: SituAnalyticsWebApiClient,
    private stateService: StateService,
    private productService: ProductService,
    private artifactService: ArtifactService) {
    this.productForm = this.formBuilder.group({
      id: [],
      manufacturerId: [],
      description: [null, Validators.required],
      sku: [null, Validators.required],
      displaySku: [],
      type: [],
      brand: [],
      brandLogoUrl: [],
      showBrandLogoOnly: [false],
      hideSku: [false],
      disabled: [null, Validators.required]
    });
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.params['productId'];
    this.isAddMode = !this.id;
    if (this.isAddMode) {
      this._subscriptions.push(
        this.route.queryParams.subscribe(params => {
          if (params["product"]) {
            this.copyProduct = JSON.parse(params['product']) as IProduct;
          }
        })
      );
    }

    this._subscriptions.push(this.stateService.state$.subscribe((state) => {
      this.selectedCompany = state.company as Company ?? undefined;
      if (!this.isAddMode) {
        this.loadProduct();
      } else {
        this.breadcrumbService.setParam('productSku', "");
        this.productForm.removeControl("id");
        if (this.copyProduct) {
          this.productForm.patchValue(this.copyProduct);
        }
      }
    }));

    const defaultCompanyCode = this.authService.getDefaultCompanyCode() ?? '';
    if (!this.company) {
      this.company = defaultCompanyCode;
    }

    this.admin = (this.authService.hasRole(Role.SituSystemsDeveloper) || this.authService.hasRole(Role.SituSystemsAdmin));

    this.productForm.get("sku")?.valueChanges.subscribe(x => {
      this.productSku = x;
      this.breadcrumbService.setParam('productSku', x);
   });
  }

  get form() { return this.productForm.controls; }

  toggleHomeFeed() {
    this.isNotHomeFeed = !this.isNotHomeFeed;
  }

  onSubmit() {
    this.submitted = true;

    if (this.productForm.invalid) {
      this.submitted = false;
      return;
    }

    if (this.isAddMode) {
      this.addProduct();
    } else {
      this.editProduct();
    }
  }

  editProduct() {
    let request = { product: this.productForm.value as IProduct } as UpdateProductRequest;
    request.companyId = this.selectedCompany?.id;
    this.situViewClient.updateProduct(request)
      .then(x => {
        if (x.success) {
          this.showMessage(`Success! ${request?.product?.description} was updated.`);
          this.breadcrumbService.setParam('productSku', request?.product?.sku);
        } else {
          this.showMessage(`Sorry we were unable to save your product ${request?.product?.description}. Please try again!`);
        }
      })
      .catch((x: any) => {
        this.showMessage(`Sorry we were unable to save your product ${request?.product?.description}. Please try again!`);
      });
  }

  addProduct() {
    let request = { product: this.productForm.value as IProduct } as AddProductRequest;
    if (request.product) request.product.companyId = this.selectedCompany?.id;
    request.companyId = this.selectedCompany?.id;
    this.situViewClient.addProduct(request)
      .then(x => {
        if (x.success && x.product) {
          this.showMessage(`Success! ${request?.product?.description} was added.`);
          this.productForm.addControl("id", new FormControl(''));
          this.productForm.patchValue(x.product);
          this.isAddMode = false;
          this.router.navigate([`/products/product/${x.product?.id}`]);
        } else {
          this.showMessage(`Sorry we were unable to add your product ${request?.product?.description}. Please try again!`);
        }
      })
      .catch((x: any) => {
        this.showMessage(`Sorry we were unable to add your product ${request?.product?.description}. Please try again!`);
      });
  }

  resetProduct() {
    if (this.isAddMode) {
      this.productForm.reset();
    } else {
      this.productForm.reset();
      this.loadProduct();
    }
  }

  showProductsView() {
    this.router.navigate(['/products/dashboard']);
  }

  getDetailTitleText(): string {
    return this.isAddMode ? "Add" : `${this.productForm.get("sku")?.value}`;
  }

  totalsLabelFormatter (tick: string) {
    let date = new Date(tick);
    return `${date.getHours()}`;
  } 

  viewsLabelFormatter (tick: string) {
    return Utils.capitalizeFirstLetter(tick);
  } 

  onClickProductArtifact(callerReferenceKey: string) {
    this.loading = true;

    let updateProductMatchRequest = new UpdateProductMatchRequest();
    updateProductMatchRequest.companyId = this.selectedCompany?.id;
    updateProductMatchRequest.productId = this.id;
    updateProductMatchRequest.callerReferenceKey = callerReferenceKey;

    this.productService.UpdateProductMatch(updateProductMatchRequest)
      .then((response: any) => {
        let updateProductMatchResponse = response as UpdateProductMatchResponse;
        if (updateProductMatchResponse.success) {
          if (this.product) {
            this.product.callerReferenceKey = callerReferenceKey;
            if (!callerReferenceKey) {
              this.loadArtifacts();
            }
          }
        } else {
          this.showMessage("There was an issue updating product match!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.showMessage("Could not update product match!");
        this.loading = false;
      });
  }
  
  getProduct(callerReferenceKey: string): string {
    return callerReferenceKey ? callerReferenceKey.split("\\").reverse()[0] : "";
  }

  isProductType(callerReferenceKey: string): boolean {
    if (callerReferenceKey) {
      let productType = callerReferenceKey.split("\\").reverse()[1];
      return productType === "product";
    }
    return true;
  }

  doFilterArtifacts = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredProductArtifacts = this.productArtifacts?.filter(e => { return e.callerReferenceKey?.toLocaleLowerCase().includes(searchTerm)});
    } else {
      this.filteredProductArtifacts = this.productArtifacts;
    }
  }

  loadArtifacts() {
    this.artifactsLoading = true;
    this.loading = true;

    let request = new ListPagedArtifactsRequest();
    request.builder = this.selectedCompany?.code;
    request.containsWords = [`${this.selectedCompany?.code?.toLowerCase()}\\product\\`, `${this.selectedCompany?.code?.toLowerCase()}\\material\\`];
    request.includeDisabled = false;
    request.pageNumber = 1;
    request.pageSize = 200;

    return this.artifactService.ListPagedArtifacts(request)
      .then((response: ListPagedArtifactsResponse) => {
        if (response.success) {
          this.productArtifacts = response.items?.
            filter(x => {
              return x?.callerReferenceKey && x.metaData?.some(m => m.key === "Type" && m.value === "assetinstance");
            }).
            sort((a, b) => (a.callerReferenceKey || '').localeCompare(b.callerReferenceKey || '')).
            map(x => new LinkableProduct(this.getProduct(x?.callerReferenceKey ?? "") ?? "", x?.callerReferenceKey ?? "", x.version ?? 0, this.isProductType(x?.callerReferenceKey ?? "")));
          this.filteredProductArtifacts = this.productArtifacts;
        } else {
          this.showMessage("There was an issue retrieving product assets!");
        }
        this.artifactsLoading = false;
        this.loading = false;
      })
      .catch((err: any) => {
        this.showMessage("Could not retrieve product assets!");
        this.artifactsLoading = false;
        this.loading = false;
      });
  }

  private loadProduct(): void {
    this.loading = true;

    let getProductRequest = new GetProductRequest();
    getProductRequest.productId = this.id;
    this.situViewClient.getProduct(getProductRequest)
      .then((response: any) => {
        let getProductResponse = response as GetProductResponse;
        if (getProductResponse.success && getProductResponse.product) {
          this.product = getProductResponse.product;
          this.productSku = getProductResponse.product.sku;
          this.productForm.patchValue(getProductResponse.product);
          this.loadPopularProductsChart();
          this.loadViewTotals();
          if (!getProductResponse?.product?.callerReferenceKey) {
            this.loadArtifacts();
          }
        } else {
          this.showMessage("There was an issue retrieving product!");
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.showMessage("Could not retrieve product!");
        this.loading = false;
      });
  }

  private loadPopularProductsChart() {
    this.viewsChartLoading = true;
    this.loading = true;

    this.viewsChartColors = [];
    this.viewsChartLabels = [];
    this.viewsChartData = [];
    this.viewsChartColors.push("rgba(56, 163, 165, 1)");
    
    let reportRequest = new GetReportRequest();
    //reportRequest.companyCode = this.selectedCompany?.code;
    reportRequest.companyId = this.selectedCompany?.id;
    reportRequest.type = ReportType.MostPopularRoom;
    reportRequest.timePeriod = 1;
    reportRequest.productSku = this.productForm.get("sku")?.value;
    return this.situAnalyticsClient.getReport(reportRequest)
      .then((renderReportResponse: any) => {
        let response = renderReportResponse as GetReportResponse;
        if (response.success) {
          let dataSet = [];
          for (let key in response.data) {
            let value = response.data[key];
            this.viewsChartLabels.push(key);
            dataSet.push(value);
          }
          this.viewsChartData = [{ data: dataSet, label: "Views" }];
        }
        this.viewsChartLoading = false;
        this.loading = false;
      })
      .catch((err: any) => {
        this.viewsChartLoading = false;
        this.loading = false;
      });
  };

  private loadViewTotals() {
    this.totalsChartLoading = true;
    this.totalsChartColors = [];
    this.totalsChartLabels = [];
    this.totalsChartData = [];
    Utils.shuffleArray(Utils.getColorPool()).forEach(color => {
      this.totalsChartColors.push(color);
    });
    
    let reportRequest = new GetReportRequest();
    //reportRequest.companyCode = this.selectedCompany?.code;
    reportRequest.companyId = this.selectedCompany?.id;
    reportRequest.type = ReportType.DailyProductViewTotal;
    reportRequest.timePeriod = 1;
    reportRequest.productSku = this.productForm.get("sku")?.value;
    return this.situAnalyticsClient.getReport(reportRequest)
      .then((renderReportResponse: any) => {
        let response = renderReportResponse as GetReportResponse;
        if (response.success) {
          let dataSet = [];
          for (let key in response.data) {
            let value = response.data[key];
            this.totalsChartLabels.push(key);
            dataSet.push(value);
          }
          this.totalsChartData = [{ data: dataSet, label: "Total" }];
        }
        this.totalsChartLoading = false;
      })
      .catch((err: any) => {
        this.totalsChartLoading = false;
      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

class LinkableProduct {
  constructor(
    public name: string,
    public callerReferenceKey: string,
    public version: number,
    public isProduct: boolean) {
  }
}